"use client"
import { useEffect, useId, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react"
import { CaretDown as ChevronDown, DotsSixVertical, Question as CircleHelp, Minus, Plus, Trash as Trash2 } from "@phosphor-icons/react"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { addFight, initializeFightPlan, MAX_FIGHTS, moveFight, type FightItem } from "@/lib/fight-plan"
import "./fight-plan-editor.css"

interface Props {
  value: FightItem[]
  onChange: (value: FightItem[]) => void
  options: {value: string; label: string}[]
  disabled?: boolean
  hideToolbar?: boolean
  helpOpen?: boolean
  onHelpOpenChange?: (open: boolean) => void
}

/** 拖拽过程中的可视状态：被拖项索引、指针位移、当前插入位、让位步长 */
interface DragState { index: number; dy: number; insert: number; slot: number }

/** 一次拖拽会话的原始数据（在 ref 中保存，避免异步事件读到过期闭包） */
interface DragSession {
  pointerId: number
  index: number
  startY: number
  rows: { top: number; height: number }[]
  slot: number
  active: boolean
  insert: number
  /** 最近一次指针纵向位移，供下一帧补测布局时沿用 */
  dy: number
  /** 布局补测的 requestAnimationFrame 句柄，拖拽结束时取消 */
  raf: number | null
}

const DRAG_GAP = 8        // 与 .fight-queue 的 gap 一致，用于计算让位步长
const DRAG_THRESHOLD = 5  // 位移超过该值才判定为拖动，避免与点击展开冲突

export function FightPlanEditor({value, onChange, options, disabled, hideToolbar, helpOpen, onHelpOpenChange}: Props) {
  const [active, setActive] = useState<number | null>(0)
  const [help, setHelp] = useState(false)
  const [openStage, setOpenStage] = useState<number | null>(null)
  const [drag, setDrag] = useState<DragState | null>(null)
  const [freeze, setFreeze] = useState(false)
  const listId = useId()
  const stageMenuRef = useRef<HTMLDivElement>(null)
  const queueRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<DragSession | null>(null)

  useEffect(() => {
    if (openStage === null) return
    const close = (event: MouseEvent) => {
      if (!stageMenuRef.current?.contains(event.target as Node)) setOpenStage(null)
    }
    document.addEventListener("mousedown", close)
    return () => document.removeEventListener("mousedown", close)
  }, [openStage])

  const update = (index: number, changes: Partial<FightItem>) => onChange(value.map((item,i) => i === index ? {...item,...changes} : item))
  const move = (index: number, direction: number) => {
    onChange(moveFight(value,index,direction)); setActive(index + direction)
  }

  // 记录各行当前几何位置（视口坐标），仅在拖拽开始时采集一次，后续用指针位移推算
  const measureRows = () => Array.from(queueRef.current?.querySelectorAll<HTMLElement>(".fight-row") ?? [])
    .map(element => { const rect = element.getBoundingClientRect(); return { top: rect.top, height: rect.height } })

  const beginDrag = (index: number) => (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (disabled || event.button !== 0) return
    dragRef.current = { pointerId: event.pointerId, index, startY: event.clientY, rows: [], slot: 0, active: false, insert: index, dy: 0, raf: null }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onDragMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const session = dragRef.current
    if (!session || event.pointerId !== session.pointerId) return
    const dy = event.clientY - session.startY
    session.dy = dy

    if (!session.active) {
      if (Math.abs(dy) < DRAG_THRESHOLD) return
      session.active = true
      if (active === session.index) {
        // 被拖项正处于展开态：先收起（--dragging 已禁用该过渡，收起是瞬时的），
        // 下一帧布局稳定后再测量，否则拿到的会是动画中间态的高度
        setActive(null)
        const index = session.index
        session.raf = requestAnimationFrame(() => {
          const current = dragRef.current
          if (!current?.active || current.index !== index) return
          current.rows = measureRows()
          current.slot = (current.rows[current.index]?.height ?? 0) + DRAG_GAP
          current.insert = current.index
          setDrag({ index: current.index, dy: current.dy, insert: current.insert, slot: current.slot })
        })
      } else {
        // 其余行都是折叠态，此刻尚未施加任何 transform，测量结果即最终布局
        session.rows = measureRows()
        session.slot = (session.rows[session.index]?.height ?? 0) + DRAG_GAP
      }
    }

    const { rows, index } = session
    if (!rows[index]) {
      // 布局尚未就绪（等待收起生效），先只做抬起反馈，让位留到补测之后
      setDrag({ index, dy, insert: session.index, slot: session.slot })
      return
    }
    // 以被拖项中心点与其余各项中线的相对位置决定插入位
    const center = rows[index].top + dy + rows[index].height / 2
    let insert = index
    if (dy < 0) {
      for (let i = index - 1; i >= 0; i--) {
        if (center < rows[i].top + rows[i].height / 2) insert = i
        else break
      }
    } else if (dy > 0) {
      for (let i = index + 1; i < rows.length; i++) {
        if (center > rows[i].top + rows[i].height / 2) insert = i
        else break
      }
    }
    session.insert = insert
    setDrag({ index, dy, insert, slot: session.slot })
  }

  const endDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const session = dragRef.current
    if (!session) return
    if (session.raf !== null) cancelAnimationFrame(session.raf)
    dragRef.current = null
    if (event.currentTarget.hasPointerCapture?.(session.pointerId)) event.currentTarget.releasePointerCapture(session.pointerId)
    if (!session.active) return  // 未超过阈值，视为普通点击，不做重排
    const { index, insert } = session
    if (insert !== index) {
      const next = [...value]
      const [moved] = next.splice(index, 1)
      next.splice(insert, 0, moved)
      onChange(next)
    }
    // 提交顺序时先冻结过渡：各行已通过位移停在目标位置，避免落位瞬间回跳
    setFreeze(true)
    setDrag(null)
    requestAnimationFrame(() => requestAnimationFrame(() => setFreeze(false)))
  }

  // 其余卡片的让位位移：插入位在下方则上移，在上方则下移
  const shiftFor = (index: number) => {
    if (!drag || drag.index === index) return 0
    if (drag.insert > drag.index && index > drag.index && index <= drag.insert) return -drag.slot
    if (drag.insert < drag.index && index >= drag.insert && index < drag.index) return drag.slot
    return 0
  }

  return <div className="fight-editor">
    {!hideToolbar && <div className="fight-toolbar"><span className="compact-muted">按 1 → {value.length || 1} 顺序执行 · {value.length}/{MAX_FIGHTS} 项</span>
      <button type="button" className="fight-help" aria-label="关卡设置帮助" onClick={() => setHelp(true)}><CircleHelp size={20} /></button>
    </div>}
    {value.length > MAX_FIGHTS && <p className="fight-warning" role="alert">旧配置超过 7 项，已完整保留，请手动调整到 7 项以内再保存。</p>}
    <div ref={queueRef} className={`fight-queue${drag ? " fight-queue--dragging" : ""}${freeze ? " fight-queue--freeze" : ""}`}>
      {value.map((item,index) => {
        const dragging = drag?.index === index
        const shift = shiftFor(index)
        const style: CSSProperties | undefined = dragging
          ? { transform: `translateY(${drag!.dy}px) scale(1.02)`, transition: "none", zIndex: 30, position: "relative" }
          : shift ? { transform: `translateY(${shift}px)` } : undefined
        return <details key={index} className={`fight-row${dragging ? " fight-row--dragging" : ""}`} open={active === index} style={style}>
        <summary onClick={e => {e.preventDefault();setActive(active === index ? null : index)}}>
          <button
            type="button"
            className="fight-grip"
            disabled={disabled}
            aria-label={`拖动排序：第 ${index+1} 项，也可用上下方向键移动`}
            onPointerDown={beginDrag(index)}
            onPointerMove={onDragMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onClick={e => {e.preventDefault();e.stopPropagation()}}
            onKeyDown={e => {
              if(e.key === "ArrowUp"){e.preventDefault();move(index,-1)}
              else if(e.key === "ArrowDown"){e.preventDefault();move(index,1)}
            }}
          ><DotsSixVertical size={16} /></button>
          <span className="fight-number">{index+1}</span><strong>{item.level || "选择关卡"}</strong><span className="fight-repeat">{Number.isFinite(item.num) ? item.num : "—"} 次</span><ChevronDown size={16} />
        </summary>
        <div className="fight-fields">
          <div className="fight-stage" ref={openStage === index ? stageMenuRef : undefined}>
            <label htmlFor={`${listId}-stage-${index}`}>关卡</label>
            <div className="fight-stage-picker">
              <input id={`${listId}-stage-${index}`} aria-label={`作战 ${index+1} 关卡`} role="combobox" aria-autocomplete="list" aria-expanded={openStage === index} aria-controls={`${listId}-options-${index}`} placeholder="选择或输入代号，如 1-7" value={item.level} onFocus={()=>setOpenStage(index)} onChange={e=>{update(index,{level:e.target.value});setOpenStage(index)}} onKeyDown={e=>{if(e.key === "Escape") setOpenStage(null); if(e.key === "ArrowDown"){e.preventDefault();setOpenStage(index)}; if(e.key === "Enter" && openStage === index){const first=options.find(o=>o.value.toLowerCase().includes(item.level.toLowerCase()) || o.label.includes(item.level)); if(first){e.preventDefault();update(index,{level:first.value});setOpenStage(null)}}}} disabled={disabled} autoComplete="off" />
              <button type="button" className="fight-stage-toggle" aria-label={`${openStage === index ? "收起" : "展开"}关卡选项`} onMouseDown={e=>e.preventDefault()} onClick={()=>setOpenStage(openStage === index ? null : index)} disabled={disabled}><ChevronDown size={16}/></button>
              {openStage === index && <div className="fight-stage-menu motion-panel" id={`${listId}-options-${index}`} role="listbox">
                {options.filter(o=>!item.level || o.value.toLowerCase().includes(item.level.toLowerCase()) || o.label.includes(item.level)).map(option=><button type="button" role="option" aria-selected={option.value === item.level} key={option.value} onClick={()=>{update(index,{level:option.value});setOpenStage(null)}}><strong>{option.value}</strong><span>{option.label}</span>{option.value === item.level && <b className="fight-option-check" aria-hidden="true">✓</b>}</button>)}
                {options.every(o=>item.level && !o.value.toLowerCase().includes(item.level.toLowerCase()) && !o.label.includes(item.level)) && <div className="fight-stage-empty">无匹配项，可直接使用当前输入</div>}
              </div>}
            </div>
          </div>
          <div className="fight-count"><label htmlFor={`${listId}-${index}`}>作战次数</label><div className="fight-stepper">
            <button type="button" aria-label={`减少作战 ${index+1} 次数`} onClick={()=>update(index,{num:Math.max(1,(Number.isFinite(item.num)?item.num:1)-1)})} disabled={disabled || item.num<=1}><Minus size={15}/></button>
            <input id={`${listId}-${index}`} aria-label={`作战 ${index+1} 次数`} type="number" min={1} max={99} step={1} value={Number.isFinite(item.num)?item.num:""} onChange={e=>update(index,{num:e.target.value===""?NaN:Number(e.target.value)})} disabled={disabled} />
            <button type="button" aria-label={`增加作战 ${index+1} 次数`} onClick={()=>update(index,{num:Math.min(99,(Number.isFinite(item.num)?item.num:0)+1)})} disabled={disabled || item.num>=99}><Plus size={15}/></button>
          </div></div>
          <div className="fight-order">
            <button type="button" className="fight-delete" onClick={()=>{onChange(value.filter((_,i)=>i!==index));setActive(null)}} disabled={disabled} aria-label={`删除作战 ${index+1}`}><Trash2 size={15}/>删除</button>
          </div>
        </div>
      </details>})}
    </div>
    <div className="fight-add-row">
      <button type="button" className="fight-add" disabled={disabled || value.length>=MAX_FIGHTS} onClick={()=>{onChange(addFight(value));setActive(value.length)}}><Plus size={17}/>添加作战{value.length>=MAX_FIGHTS?"（已达上限）":""}</button>
      {value.length===0 && <button type="button" className="fight-add" disabled={disabled} onClick={()=>{onChange(initializeFightPlan());setActive(0)}}>填入默认五项</button>}
    </div>
    <Dialog open={helpOpen ?? help} onOpenChange={onHelpOpenChange ?? setHelp}>
      <DialogContent aria-describedby="fight-help-content" className="fight-help-dialog">
        <DialogTitle>关卡怎么设置？</DialogTitle>
        <div id="fight-help-content" className="fight-help-content">
          <p><strong>选择关卡：</strong>点开作战项，在输入框选择常用关卡，也可直接输入代号，如 1-7、11-12。关卡是否可用取决于执行端和游戏当前开放情况。</p>
          <p><strong>设置顺序：</strong>拖动每项左侧的手柄即可调整先后，拖动时其余项目会自动让出空位，松手后顺序即更新；键盘用户可聚焦手柄后用上下方向键移动。顺序会连同关卡与次数一起调整，最多 7 项。速通的轮次作战会按顺序执行，最后一项结束后从头循环，直到理智耗尽等停止条件生效。</p>
          <p><strong>设置次数：</strong>本面板将关卡与次数分开填写，范围 1–99。例如关卡填 1-7、次数填 3，不必再输入 1-7x3。次数是每轮配置，不是整个任务的总次数上限。</p>
          <p><strong>默认五项：</strong>jm ×1 → hd ×99 → ce ×99 → ls ×99 → 1-7 ×99。已有配置不会被自动替换。</p>
          <p><strong>常用简写：</strong>jm（剿灭）、hd（活动材料关）、ce（龙门币）、ls（经验卡）、ap（红票）、ca（技能书）。这些是自动选关规则，并非某一个固定关卡。</p>
          <p><strong>简写的次数限制：</strong>官方说明不允许在简写后追加次数，jm 还需配合执行端的“最大剿灭次数”。当前面板保留上述默认次数，但不能据此保证简写的实际执行次数；需要指定关卡和次数时，请填写 CE-6、LS-6、1-7 等具体关卡。</p>
          <p><strong>关卡被跳过：</strong>未开放、不能代理或反复导航／代理失败的关卡可能被跳过；剿灭合成玉奖励已满时也会跳过。</p>
          <p><strong>最后保存：</strong>点击页面上方“保存配置”才会提交；编辑和排序不会立即开始作战。</p>
        </div>
      </DialogContent>
    </Dialog>
  </div>
}
